// Experience.js
import styles from "./Experience.module.css";
import img_playmint from "../assets/images/experience/playmint.jpeg";
import img_locusamoenus from "../assets/images/experience/locusamoenus.webp";
import img_validusre from "../assets/images/experience/validusre.jpeg";
import img_tlf from "../assets/images/experience/tlf.jpeg";
import img_radioplayer from "../assets/images/experience/radioplayer.webp";

import { differenceInMonths, parse } from "date-fns";
import { FiExternalLink, FiMapPin } from "react-icons/fi";
import { PiBriefcase, PiGlobeHemisphereWest } from "react-icons/pi";

const experience = [
  {
    role: "Full Stack Developer",
    company: "Radioplayer Worldwide Ltd",
    jobFreq: "Full-time",
    duration: "Jun 2025 - Present",
    location: "London, England · Hybrid",
    img: img_radioplayer,
    link: `https://radioplayer.org/`,
    summary:
      "Building full stack product work for digital radio experiences used across markets and devices.",
  },
  {
    role: "Programmer",
    company: "Playmint",
    jobFreq: "Full-time",
    duration: "Dec 2023 - Jan 2025",
    location: "Brighton, England · Hybrid",
    img: img_playmint,
    link: `https://playmint.com/`,
    summary:
      "Worked across blockchain game systems, frontend tooling, and product features for Downstream and internal workflows.",
  },
  {
    role: "Co-Founder and Lead Programmer",
    company: "Locus Amoenus",
    jobFreq: "Freelance",
    duration: "Jun 2020 - Dec 2023",
    location: "Milan, Italy · Remote",
    img: img_locusamoenus,
    link: `https://www.fiverr.com/locusamoenus/develop-your-3d-game-on-unity`,
    summary:
      "Co-ran a freelance studio delivering client games, prototypes, and technical builds for a repeat international client base.",
  },
  {
    role: "Back End Software Engineer",
    company: "Validus Reinsurance",
    jobFreq: "Internship",
    duration: "Jun 2021 - Sep 2021",
    location: "Hamilton, Bermuda · Remote",
    img: img_validusre,
    link: `https://www.linkedin.com/company/validus/`,
    summary:
      "Built backend APIs and internal tools for underwriters working with financial and data-heavy workflows.",
  },
  {
    role: "Front End UI Engineer",
    company: "Technology Leadership Forum",
    jobFreq: "Internship",
    duration: "Jun 2021 - Sep 2021",
    location: "Hamilton, Bermuda · On-site",
    img: img_tlf,
    link: `https://www.tlf.bm/`,
    summary:
      "Delivered frontend UI work and interactive web experiences in a client-facing internship environment.",
  },
];

function calculateDuration(duration) {
  const [start, end] = duration.split(" - ");
  const startDate = parse(start, "MMM yyyy", new Date());
  const endDate =
    end === "Present" ? new Date() : parse(end, "MMM yyyy", new Date());
  const totalMonths = differenceInMonths(endDate, startDate) + 1;
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const formatted = [
    years > 0 ? `${years} year${years > 1 ? "s" : ""}` : "",
    months > 0 ? `${months} month${months > 1 ? "s" : ""}` : "",
  ]
    .filter(Boolean)
    .join(", ");
  return formatted || "0 months";
}

function getLocationBase(location) {
  return location.split(" · ")[0];
}

function getWorkMode(location) {
  return location.split(" · ")[1] || "";
}

const currentRole = experience.find((entry) => entry.duration.includes("Present"));
const uniqueLocations = new Set(experience.map((entry) => getLocationBase(entry.location)));
const experienceStats = [
  {
    label: "Current role",
    value: currentRole ? `${currentRole.role} at ${currentRole.company}` : "Active",
    icon: PiBriefcase,
  },
  {
    label: "Roles",
    value: `${experience.length} across product, games, and finance`,
    icon: PiBriefcase,
  },
  {
    label: "Regions",
    value: `${uniqueLocations.size} international bases`,
    icon: PiGlobeHemisphereWest,
  },
];

function Card({
  experience: { role, company, jobFreq, duration, location, img, link, summary },
}) {
  const formattedDuration = `${duration} (${calculateDuration(duration)})`;
  const workMode = getWorkMode(location);
  const isCurrent = duration.includes("Present");
  return (
    <div className={styles.card}>
      <div className={styles.cardRail}>
        <span className={styles.railDot} />
      </div>
      <div className={styles.cardMedia}>
        <img src={img} alt={`${company} logo`} />
      </div>
      <div className={styles.right}>
        <div className={styles.cardTopRow}>
          <div className={styles.roleTitle}>{role}</div>
          {isCurrent && <div className={styles.currentTag}>Current</div>}
        </div>
        <div className={styles.jobRow}>
          <a href={link} target="_blank" rel="noopener noreferrer">
            {company} <FiExternalLink />
          </a>
        </div>
        <div className={styles.metaRow}>
          <span className={styles.metaPill}>{jobFreq}</span>
          {workMode && <span className={styles.metaPill}>{workMode}</span>}
          <span className={styles.metaPill}>{formattedDuration}</span>
        </div>
        <div className={styles.location}>
          <FiMapPin /> {getLocationBase(location)}
        </div>
        <div className={styles.summary}>{summary}</div>
      </div>
    </div>
  );
}

function Experience() {
  return (
    <div className={styles.experience}>
      <p className={styles.intro}>
        Roles across media, games, finance, and freelance product development,
        with a consistent focus on shipping reliable systems and polished user
        experiences.
      </p>
      <div className={styles.stats}>
        {experienceStats.map(({ label, value, icon: Icon }) => (
          <div key={label} className={styles.statCard}>
            <Icon className={styles.statIcon} />
            <div className={styles.statLabel}>{label}</div>
            <div className={styles.statValue}>{value}</div>
          </div>
        ))}
      </div>
      <div className={styles.cards}>
        {experience.map((e, i) => (
          <Card key={i} experience={e} />
        ))}
      </div>
    </div>
  );
}

export default Experience;
